import { Loader2, Phone, Target } from 'lucide-react'
import { useSearchParams } from 'react-router'
import { useTranslation } from 'react-i18next'
import { cn } from '@/lib/utils'
import { useUIStore } from '@/stores/useUIStore'
import { usePowerDialerStatus } from '@/api/powerDialer'
import { ActivationView } from './powerDialer/ActivationView'
import { CampaignListView } from './powerDialer/CampaignListView'
import { CampaignFormView } from './powerDialer/CampaignFormView'
import { PdNumbersView } from './powerDialer/PdNumbersView'

type PdSection = 'campaigns' | 'numbers'

export function PowerDialerView() {
  const { t } = useTranslation()
  const darkMode = useUIStore((s) => s.darkMode)
  const { data, isLoading, refetch } = usePowerDialerStatus()
  const [searchParams, setSearchParams] = useSearchParams()

  // Sous-vue stockée dans l'URL :
  //  - ?pdView=create | edit  → wizard de campagne
  //  - ?pdSection=campaigns | numbers  → toggle entre les 2 onglets principaux
  const pdView = searchParams.get('pdView') // 'create' | 'edit' | null
  const pdId = Number(searchParams.get('pdId') || '') || null
  const section: PdSection =
    (searchParams.get('pdSection') as PdSection) || 'campaigns'

  const setPdView = (view: 'create' | 'edit' | null, id?: number) => {
    const next = new URLSearchParams(searchParams)
    if (view === null) {
      next.delete('pdView')
      next.delete('pdId')
    } else {
      next.set('pdView', view)
      if (id != null) next.set('pdId', String(id))
      else next.delete('pdId')
    }
    setSearchParams(next)
  }

  const setSection = (next: PdSection) => {
    const params = new URLSearchParams(searchParams)
    if (next === 'campaigns') params.delete('pdSection')
    else params.set('pdSection', next)
    // Au switch de section, on quitte le wizard d'édition pour éviter
    // l'incohérence visuelle (form ouvert sur l'onglet Numéros).
    params.delete('pdView')
    params.delete('pdId')
    setSearchParams(params)
  }

  if (isLoading) {
    return (
      <div className="h-full flex items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
      </div>
    )
  }

  const enabled = data?.power_dialer_enabled ?? false

  return (
    <div className="h-full flex flex-col">
      {/* Tabs Campagnes | Numéros (masqués si pas activé ou en mode wizard) */}
      {enabled && !pdView && (
        <div
          className={cn(
            'flex-shrink-0 border-b',
            darkMode
              ? 'border-slate-800 bg-slate-900'
              : 'border-slate-200 bg-white',
          )}
        >
          <div className="px-6 lg:px-8">
            <nav className="flex gap-1" role="tablist">
              <SectionTab
                active={section === 'campaigns'}
                onClick={() => setSection('campaigns')}
                icon={<Target className="h-4 w-4" />}
                label={t('manager.powerDialer.sections.campaigns', 'Campagnes')}
                darkMode={darkMode}
              />
              <SectionTab
                active={section === 'numbers'}
                onClick={() => setSection('numbers')}
                icon={<Phone className="h-4 w-4" />}
                label={t('manager.powerDialer.sections.numbers', 'Numéros')}
                darkMode={darkMode}
              />
            </nav>
          </div>
        </div>
      )}

      <div className="flex-1 min-h-0 overflow-y-auto">
        <div className="px-6 lg:px-8 py-6">
          {!enabled ? (
            <ActivationView onActivated={() => void refetch()} />
          ) : pdView === 'create' ? (
            <CampaignFormView onBack={() => setPdView(null)} />
          ) : pdView === 'edit' && pdId ? (
            <CampaignFormView
              campaignId={pdId}
              onBack={() => setPdView(null)}
            />
          ) : section === 'numbers' ? (
            <PdNumbersView />
          ) : (
            <CampaignListView
              onCreate={() => setPdView('create')}
              onEdit={(id) => setPdView('edit', id)}
            />
          )}
        </div>
      </div>
    </div>
  )
}

interface SectionTabProps {
  active: boolean
  onClick: () => void
  icon: React.ReactNode
  label: string
  darkMode: boolean
}

function SectionTab({ active, onClick, icon, label, darkMode }: SectionTabProps) {
  return (
    <button
      type="button"
      role="tab"
      aria-selected={active}
      onClick={onClick}
      className={cn(
        'flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 -mb-px transition-colors',
        active
          ? darkMode
            ? 'border-white text-white'
            : 'border-[#0A192F] text-[#0A192F]'
          : darkMode
            ? 'border-transparent text-slate-400 hover:text-white hover:border-slate-700'
            : 'border-transparent text-slate-500 hover:text-slate-900 hover:border-slate-300',
      )}
    >
      {icon}
      {label}
    </button>
  )
}
